"use client";
import type { ReactNode } from "react";
import { useRef, useEffect } from "react";
import { AssistantRuntimeProvider, ChatModelAdapter, useLocalRuntime } from "@assistant-ui/react";
import { getToken } from "@/utils/localstorage";
import { useAlphaAgentStore } from "@/state/alphaAgentStore";
import axios from "axios";
import { parseTextAndImages } from "@/placeholderPopup/components/MyRuntimeProviderFeatureDetails";

const apiUrl = process.env.API_URL;

export const MyModelAdapter: ChatModelAdapter = {
  async *run({ messages }): AsyncGenerator {
    const authToken = await getToken();

    const lastMessage = messages[messages.length - 1];
    let userInput = "";

    if (lastMessage?.content && Array.isArray(lastMessage.content)) {
      const textPart = lastMessage.content.find(
        (part) => typeof part === "object" && "text" in part && typeof part.text === "string"
      );
      if (textPart) userInput = (textPart as any).text;
    }

    const isInitialization = userInput === "__INIT_FEATURE_DETAILS_EDIT__";

    let result;
    // Signal run start before any network requests
    try {
      window.dispatchEvent(new CustomEvent("ai-run-start"));
    } catch (e) {}
    if (isInitialization) {
      // Get the features data for initialization
      const { featuresData } = useAlphaAgentStore.getState();

      result = await axios.post(
        `${apiUrl}/alpha-edit/interact`,
        {
          userInput: featuresData,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
    } else {
      result = await axios.post(
        `${apiUrl}/alpha-edit/interact`,
        { userInput },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
    }

    localStorage.setItem("alphaCreateData", JSON.stringify(result.data.data.globalState));
    const {
      markAnswered,
      getSelectedFeature,
      setSelectedFeature,
      setDisableBackButton,
      setSelectedFeatureMessagesData,
      setDisabledButton,
      updateAnswerByStep,
    } = useAlphaAgentStore.getState();

    setSelectedFeatureMessagesData(result.data.data.currentForm, messages[messages.length - 2]);
    setSelectedFeatureMessagesData(result.data.data.currentForm, messages[messages.length - 1]);

    const step = 2;
    const globalState = result.data.data.globalState;

    const featureFormMap: Record<string, string> = {
      "percentage-change-chart": "percentageChangeChartForm",
      "constant-value-line": "constantValueLineForm",
      "custom-built-indices": "customBuiltIndicesForm",
      "vertical-line": "verticalLineForm",
      "background-shading": "backgroundShadingForm",
      "call-out-boxes": "callOutBoxesForm",
      "summary-table": "summaryTableForm",
      "small-line-chart": "smallLineChartForm",
    };
    Object.entries(featureFormMap).forEach(([featureKey, formKey]) => {
      if (globalState[formKey] && getSelectedFeature(featureKey) !== true) {
        markAnswered(step);
        setSelectedFeature(featureKey, true);
      }
    });

    console.log(result.data.data.step);
    if (result.data.data.step === 4) {
      setDisableBackButton(true);
      markAnswered(step);
    } else {
      updateAnswerByStep(step, 0);
      setDisabledButton(true);
      console.log("Run");
    }

    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    const fullText = result.data.data.response;
    const segments = parseTextAndImages(fullText);

    for (let i = 1; i <= segments.length; i++) {
      const currentSegments = segments.slice(0, i);
      const currentText = currentSegments.join("");
      if (i === 1) {
        try {
          window.dispatchEvent(new CustomEvent("ai-first-token"));
        } catch (e) {}
      }
      yield {
        content: [{ type: "text", text: currentText }],
      };
      await delay(8);
    }
    try {
      window.dispatchEvent(new CustomEvent("ai-run-end"));
    } catch (e) {}
  },
};

export function MyRuntimeProviderFeatureDetails({ children }: { children: ReactNode }) {
  return <MyRuntimeProviderReady>{children}</MyRuntimeProviderReady>;
}

function MyRuntimeProviderReady({ children }: { children: ReactNode }) {
  const runtime = useLocalRuntime(MyModelAdapter, {
    initialMessages: [],
  });

  // Trigger initialize after mount to show the initial message with streaming
  const initSentRef = useRef(false);
  useEffect(() => {
    if (initSentRef.current) return;

    try {
      const thread = (runtime as any)?.thread;
      if (thread && typeof thread.append === "function") {
        thread.append({
          role: "system",
          content: [{ type: "text", text: "__INIT_FEATURE_DETAILS_EDIT__" }],
          startRun: true,
        });
        initSentRef.current = true;
      }
    } catch (e) {}
  }, [runtime]);

  return (
    <AssistantRuntimeProvider key="feature-details-edit" runtime={runtime}>
      {children}
    </AssistantRuntimeProvider>
  );
}
